import type { MascotPose } from '@xdocs/ui';
import type { FtueCatalogKey } from './ftue.i18n.js';

/**
 * FTUE (F9) step config — 6 steps covering FEATURES.md's list (generate/
 * F1/F-Estep/F-Echat, confirm-structure F15 + killer features F2/F3/F4,
 * explorer+uploads F7/F18, credits F19, discovery/sharing F27) condensed
 * into the "4-6 steps highlighting generate/documents/credits/share" this
 * task's brief calls for; final step is the celebratory "Start creating" CTA.
 *
 * `targetSelector`, when present, names a `data-ftue-target="<value>"`
 * attribute this step spotlights (F9: "spotlight on a real UI element").
 * NONE of today's app-shell components carry that attribute yet — adding it
 * touches `apps/web/src/app`/nav/shell files, which are outside this
 * feature's ownership (see this task's returned open-issues list). `
 * <FtueTour>` degrades gracefully per the F9 edge case ("coach-marks must
 * target real, present elements — don't point at a control that isn't
 * rendered yet"): when a selector resolves to nothing, the step renders as
 * a centered card with NO highlight ring, never a broken/misplaced spotlight.
 */
export interface FtueStep {
  id: string;
  pose: MascotPose;
  /** Space Mono uppercase progress caption ("Step 1 · Generate") shown above
   * the mascot — mirrors the `eyebrow` field on every step of the
   * `17-ftue-onboarding.html` mockup's `FTUE_STEPS` data (mockup-01: the mock
   * gives every step, not just "welcome", its own eyebrow). */
  eyebrowKey: FtueCatalogKey;
  titleKey: FtueCatalogKey;
  bodyKey: FtueCatalogKey;
  targetSelector?: string;
}

export const FTUE_STEPS: readonly FtueStep[] = [
  {
    id: 'welcome',
    pose: 'waving',
    eyebrowKey: 'eyebrow.welcome',
    titleKey: 'step.welcome.title',
    bodyKey: 'step.welcome.body',
  },
  {
    id: 'generate',
    pose: 'pointing',
    eyebrowKey: 'eyebrow.generate',
    titleKey: 'step.generate.title',
    bodyKey: 'step.generate.body',
    targetSelector: '[data-ftue-target="generate"]',
  },
  {
    id: 'features',
    pose: 'thinking',
    eyebrowKey: 'eyebrow.features',
    titleKey: 'step.features.title',
    bodyKey: 'step.features.body',
  },
  {
    id: 'documents',
    pose: 'reading',
    eyebrowKey: 'eyebrow.documents',
    titleKey: 'step.documents.title',
    bodyKey: 'step.documents.body',
    targetSelector: '[data-ftue-target="documents"]',
  },
  {
    id: 'credits',
    pose: 'idle',
    eyebrowKey: 'eyebrow.credits',
    titleKey: 'step.credits.title',
    bodyKey: 'step.credits.body',
    targetSelector: '[data-ftue-target="credits"]',
  },
  {
    id: 'share',
    pose: 'celebrating',
    eyebrowKey: 'eyebrow.share',
    titleKey: 'step.share.title',
    bodyKey: 'step.share.body',
  },
] as const;
